import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminTopBar, ViewScroll, Panel, StatCard, Sym } from '../../ui';
import { fetchMessages, createMessage } from '../../api/messages';
import { fetchUsers } from '../../api/users';
import {
  UserRole,
  type BulkMessage,
  type MessageChannel,
  type MessageTarget,
  type CreateMessageInput,
  type UserInfo,
} from '@grosync/common';

/* ============================================================================
 * 메시지발송 — 회원 단체 문자 / 알림톡
 * 발송 이력은 백엔드 messages API(/api/messages)에서 가져온다.
 * 수신자 수는 users API에서 실시간 집계한다.
 * byte/segment/cost 라이브 미리보기는 서버 로직을 미러링해 클라이언트에서 계산한다.
 * ========================================================================= */

type CSS = React.CSSProperties;

type ChannelKey = MessageChannel;
interface ChannelMeta {
  k: ChannelKey;
  label: string;
  desc: string;
  icon: string;
  price: number;
  limit: number;
}

const MSG_CHANNELS: ChannelMeta[] = [
  { k: 'sms', label: 'SMS', desc: '단문 · 90바이트', icon: 'sms', price: 9, limit: 90 },
  { k: 'lms', label: 'LMS', desc: '장문 · 2,000바이트', icon: 'article', price: 30, limit: 2000 },
  { k: 'kakao', label: '알림톡', desc: '카카오 · 템플릿', icon: 'forum', price: 8, limit: 1000 },
];
const msgCh = (k: ChannelKey): ChannelMeta => MSG_CHANNELS.find((c) => c.k === k) ?? MSG_CHANNELS[0];

// EUC-KR 근사 바이트 수 (한글 2 · ASCII 1)
const byteLen = (s: string): number => [...s].reduce((n, ch) => n + (ch.charCodeAt(0) > 127 ? 2 : 1), 0);

interface RecipientGroup {
  k: MessageTarget;
  label: string;
  icon: string;
}

// 수신 대상 그룹 라벨/아이콘 (수치는 실 users API에서 집계)
const GROUPS: RecipientGroup[] = [
  { k: 'all', label: '전체 회원', icon: 'group' },
  { k: 'users', label: '사용자', icon: 'person' },
  { k: 'managers', label: '담당자', icon: 'shield_person' },
  { k: 'active', label: '활성 회원', icon: 'how_to_reg' },
  { k: 'pending', label: '승인대기 회원', icon: 'hourglass_top' },
];

const SENDERS = ['1666-2026', '02-555-0180', '070-4188-2026'];

const TARGET_LABELS: Record<MessageTarget, string> = {
  all: '전체 회원',
  users: '사용자',
  managers: '담당자',
  active: '활성 회원',
  pending: '승인대기 회원',
};

/** ISO 문자열 → "MM.DD HH:mm" 표기 */
const fmtAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const msgInput: CSS = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 12,
  boxSizing: 'border-box',
  border: '1.5px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-low)',
  font: '500 15px/22px var(--gs-font-kr)',
  color: 'var(--md-sys-color-on-surface)',
  outline: 'none',
};

function MsgLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 8 }}>
      {children}
    </div>
  );
}

export default function MessageView(): React.ReactElement {
  const qc = useQueryClient();
  const messagesQ = useQuery({ queryKey: ['messages'], queryFn: fetchMessages });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: fetchUsers });

  const [channel, setChannel] = useState<ChannelKey>('sms');
  const [target, setTarget] = useState<MessageTarget>('all');
  const [sender, setSender] = useState('1666-2026');
  const [body, setBody] = useState('[GroVision] ');
  const [timing, setTiming] = useState<'now' | 'schedule'>('now');
  const [at, setAt] = useState('2026-06-14T09:00');
  const [toast, setToast] = useState<string | null>(null);

  // 실 사용자 기반 대상별 수신자 수 (서버 집계 로직과 동일)
  const groupCounts = useMemo<Record<MessageTarget, number>>(() => {
    const users: UserInfo[] = usersQ.data ?? [];
    const active = users.filter((u) => u.isActive);
    return {
      all: active.length,
      users: active.filter((u) => u.role === UserRole.VIEWER).length,
      managers: active.filter((u) => u.role === UserRole.OPERATOR).length,
      active: users.filter((u) => u.isActive).length,
      pending: users.filter((u) => !u.isActive).length,
    };
  }, [usersQ.data]);

  const messages = messagesQ.data ?? [];

  const cMeta = msgCh(channel);
  const group = GROUPS.find((g) => g.k === target) ?? GROUPS[0];
  const cnt = groupCounts[target];
  const bytes = byteLen(body);
  const overLimit = bytes > cMeta.limit;
  const segments = channel === 'sms' ? Math.max(1, Math.ceil(bytes / 90)) : 1;
  const cost = cnt * cMeta.price * (channel === 'sms' ? segments : 1);

  const canSend = body.trim().length > 0 && cnt > 0 && !overLimit;

  // StatCards: 발송 이력 기반 집계
  const stats = useMemo(() => {
    const now = new Date();
    const month = messages.filter((m) => {
      const d = new Date(m.createdAt);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    const monthSegments = month.reduce((s, m) => s + m.recipientCount * m.segments, 0);
    const lastRecipients = messages[0]?.recipientCount ?? 0;
    const scheduled = messages.filter((m) => m.status === 'scheduled').length;
    return { monthCount: month.length, monthSegments, lastRecipients, scheduled };
  }, [messages]);

  const createM = useMutation({
    mutationFn: (input: CreateMessageInput) => createMessage(input),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      const label = msgCh(created.channel).label;
      setToast(
        created.status === 'scheduled'
          ? `${fmtAt(created.scheduledAt ?? created.createdAt)}에 ${created.recipientCount.toLocaleString()}건 발송 예약되었습니다.`
          : `${created.recipientCount.toLocaleString()}명에게 ${label} 발송을 시작했습니다.`,
      );
      window.setTimeout(() => setToast(null), 4000);
    },
    onError: (err: unknown) => {
      setToast(`메시지 발송에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
      window.setTimeout(() => setToast(null), 4000);
    },
  });

  const send = (): void => {
    if (!canSend || createM.isPending) return;
    const input: CreateMessageInput = {
      channel,
      target,
      content: body.trim(),
      senderNumber: sender,
      scheduledAt: timing === 'schedule' ? new Date(at).toISOString() : null,
    };
    createM.mutate(input);
  };

  const HIST_COLS = '0.7fr 1fr 2.6fr 0.7fr 1fr 0.7fr';

  return (
    <>
      <AdminTopBar title="메시지발송" subtitle="회원에게 단체 문자 · 알림톡을 발송합니다" />

      <ViewScroll gap={18}>
        {toast && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '13px 18px',
              borderRadius: 14,
              background: 'var(--md-sys-color-primary-container)',
              color: 'var(--md-sys-color-on-primary-container)',
              font: '600 14px/20px var(--gs-font-kr)',
            }}
          >
            <Sym name="check_circle" size={20} fill={1} />
            {toast}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          <StatCard icon="send" label="이번 달 발송" value={stats.monthSegments.toLocaleString()} unit="건" tone="secondary" sub={`발송 ${stats.monthCount}회`} />
          <StatCard icon="group" label="발송 가능 회원" value={groupCounts.all.toLocaleString()} unit="명" tone="primary" sub="활성 회원 기준" />
          <StatCard icon="schedule_send" label="예약 발송" value={stats.scheduled} unit="건" tone="muted" sub={`최근 수신 ${stats.lastRecipients.toLocaleString()}명`} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(300px, 0.75fr)', gap: 18, alignItems: 'start' }}>
          {/* compose */}
          <Panel title="메시지 작성" subtitle="채널과 수신 대상을 선택하세요">
            {/* channel */}
            <div style={{ marginBottom: 18 }}>
              <MsgLabel>발송 채널</MsgLabel>
              <div style={{ display: 'flex', gap: 10 }}>
                {MSG_CHANNELS.map((c) => {
                  const a = channel === c.k;
                  return (
                    <button
                      key={c.k}
                      type="button"
                      onClick={() => setChannel(c.k)}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                        padding: '13px 15px',
                        borderRadius: 14,
                        border: a ? '2px solid var(--md-sys-color-primary)' : '1.5px solid var(--md-sys-color-outline-variant)',
                        background: a ? 'var(--md-sys-color-primary-container)' : 'var(--md-sys-color-surface-container-lowest)',
                      }}
                    >
                      <Sym name={c.icon} size={22} fill={a ? 1 : 0} style={{ color: a ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)' }} />
                      <div style={{ font: '700 14px/18px var(--gs-font-kr)', color: a ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface)' }}>{c.label}</div>
                      <div style={{ font: '400 11px/14px var(--gs-font-kr)', color: a ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)', opacity: 0.9 }}>{c.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* target */}
            <div style={{ marginBottom: 18 }}>
              <MsgLabel>수신 대상</MsgLabel>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {GROUPS.map((g) => {
                  const a = target === g.k;
                  return (
                    <button
                      key={g.k}
                      type="button"
                      onClick={() => setTarget(g.k)}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 7,
                        height: 40,
                        padding: '0 14px',
                        borderRadius: 9999,
                        border: a ? 'none' : '1.5px solid var(--md-sys-color-outline-variant)',
                        background: a ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                        color: a ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                        font: `${a ? 700 : 500} 13px/1 var(--gs-font-kr)`,
                      }}
                    >
                      <Sym name={g.icon} size={16} fill={a ? 1 : 0} />
                      {g.label}
                      <span className="gs-num" style={{ fontWeight: 700, opacity: a ? 1 : 0.7 }}>{groupCounts[g.k].toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* sender */}
            <div style={{ marginBottom: 18 }}>
              <MsgLabel>발신번호</MsgLabel>
              <select value={sender} onChange={(e) => setSender(e.target.value)} style={{ ...msgInput, cursor: 'pointer' }}>
                {SENDERS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>

            {/* message */}
            <div style={{ marginBottom: 18 }}>
              <MsgLabel>메시지 내용</MsgLabel>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                placeholder="발송할 메시지를 입력하세요. 광고성 메시지는 (광고) 표기와 무료수신거부 번호가 필요합니다."
                style={{
                  ...msgInput,
                  height: 'auto',
                  padding: '12px 14px',
                  lineHeight: '22px',
                  resize: 'vertical',
                  border: `1.5px solid ${overLimit ? 'var(--gs-error)' : 'var(--md-sys-color-outline-variant)'}`,
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                <span style={{ font: '500 11px/15px var(--gs-font-kr)', color: overLimit ? 'var(--gs-error)' : 'var(--md-sys-color-on-surface-variant)' }}>
                  {overLimit
                    ? `${cMeta.label} 한도(${cMeta.limit.toLocaleString()}바이트)를 초과했습니다`
                    : channel === 'sms' && segments > 1
                      ? '90바이트 초과 — LMS 전환 권장'
                      : `${cMeta.label} · 한도 ${cMeta.limit.toLocaleString()}바이트`}
                </span>
                <span className="gs-num" style={{ font: '600 11px/15px var(--gs-font-kr)', color: overLimit ? 'var(--gs-error)' : 'var(--md-sys-color-on-surface-variant)' }}>
                  {bytes}/{cMeta.limit.toLocaleString()} byte{channel === 'sms' && segments > 1 ? ` · ${segments}건 분할` : ''}
                </span>
              </div>
            </div>

            {/* timing */}
            <div style={{ marginBottom: 18 }}>
              <MsgLabel>발송 시점</MsgLabel>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', padding: 4, borderRadius: 9999, background: 'var(--md-sys-color-surface-container-high)' }}>
                  {([['now', '즉시 발송'], ['schedule', '예약 발송']] as const).map(([k, l]) => {
                    const a = timing === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setTiming(k)}
                        style={{
                          height: 34,
                          padding: '0 16px',
                          borderRadius: 9999,
                          border: 0,
                          cursor: 'pointer',
                          background: a ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                          color: a ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                          font: `${a ? 700 : 500} 13px/1 var(--gs-font-kr)`,
                          boxShadow: a ? 'var(--md-sys-elevation-1)' : 'none',
                        }}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
                {timing === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={at}
                    onChange={(e) => setAt(e.target.value)}
                    style={{ ...msgInput, width: 'auto', height: 42, flex: 1, minWidth: 200 }}
                  />
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              style={{
                width: '100%',
                height: 52,
                borderRadius: 14,
                border: 0,
                background: canSend ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)',
                color: canSend ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface-variant)',
                font: '700 15px/22px var(--gs-font-kr)',
                cursor: canSend ? 'pointer' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Sym name={timing === 'now' ? 'send' : 'schedule_send'} size={20} />
              {timing === 'now' ? `${cnt.toLocaleString()}명에게 ${cMeta.label} 발송` : '발송 예약'}
            </button>
          </Panel>

          {/* preview + summary */}
          <Panel title="미리보기" subtitle={`${cMeta.label} · ${group.label}`}>
            {/* phone bubble */}
            <div style={{ borderRadius: 18, padding: '16px 14px', background: 'var(--md-sys-color-surface-container-high)' }}>
              <div style={{ textAlign: 'center', font: '500 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: 12 }}>
                {channel === 'kakao' ? '카카오 알림톡' : '문자 메시지'} · {sender}
              </div>
              <div
                style={{
                  maxWidth: '85%',
                  padding: '11px 13px',
                  borderRadius: '4px 16px 16px 16px',
                  background: channel === 'kakao' ? '#FEE500' : 'var(--md-sys-color-surface-container-lowest)',
                  border: channel === 'kakao' ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  color: channel === 'kakao' ? '#3A2929' : 'var(--md-sys-color-on-surface)',
                }}
              >
                {channel === 'kakao' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, paddingBottom: 7, marginBottom: 7, borderBottom: '1px solid rgba(0,0,0,0.12)', font: '700 11px/14px var(--gs-font-kr)' }}>
                    <Sym name="forum" size={13} fill={1} />
                    GroVision 알림톡
                  </div>
                )}
                <div style={{ font: '400 13px/20px var(--gs-font-kr)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textWrap: 'pretty' }}>
                  {body.trim() || '메시지 내용이 여기에 표시됩니다.'}
                </div>
              </div>
              <div style={{ font: '400 10px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 6, marginLeft: 4 }}>지금 · 전송됨</div>
            </div>

            {/* recipients summary */}
            <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 14, background: 'var(--md-sys-color-surface-container-low)', border: '1px solid var(--md-sys-color-outline-variant)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>수신 대상</span>
                <span className="gs-num" style={{ font: '700 18px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-primary)' }}>
                  {cnt.toLocaleString()}
                  <span style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginLeft: 2 }}>명</span>
                </span>
              </div>
              <div style={{ font: '400 12px/17px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginTop: 6 }}>
                {group.label} {cnt.toLocaleString()}명에게 발송됩니다.
              </div>
            </div>

            {/* cost estimate */}
            <div style={{ marginTop: 12, padding: '14px 16px', borderRadius: 14, background: 'var(--md-sys-color-surface-container-low)', border: '1px solid var(--md-sys-color-outline-variant)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(
                [
                  ['건당 단가', `${cMeta.price}원${channel === 'sms' && segments > 1 ? ` × ${segments}건` : ''}`],
                  ['발송 건수', `${(cnt * (channel === 'sms' ? segments : 1)).toLocaleString()}건`],
                ] as const
              ).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  <span>{k}</span>
                  <span className="gs-num" style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ height: 1, background: 'var(--md-sys-color-outline-variant)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ font: '600 14px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>예상 비용</span>
                <span className="gs-num" style={{ font: '700 20px/24px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
                  {cost.toLocaleString()}
                  <span style={{ font: '500 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginLeft: 2 }}>원</span>
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* history */}
        <Panel title="발송 이력" subtitle={`최근 ${messages.length}건`} pad={false}>
          <div className="gs-scroll-hide" style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 760 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: HIST_COLS,
                  padding: '12px 22px',
                  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-low)',
                }}
              >
                {['채널', '대상', '내용', '건수', '발송일시', '상태'].map((h, i) => (
                  <span
                    key={h}
                    style={{
                      font: '600 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      textAlign: i === 3 ? 'right' : 'left',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>
              {messagesQ.isLoading && (
                <div style={{ padding: '28px 22px', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  불러오는 중…
                </div>
              )}
              {!messagesQ.isLoading && messages.length === 0 && (
                <div style={{ padding: '28px 22px', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  발송 이력이 없습니다
                </div>
              )}
              {messages.map((r: BulkMessage, i) => {
                const cm = msgCh(r.channel);
                const scheduled = r.status === 'scheduled';
                return (
                  <div
                    key={r.id}
                    className="gs-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: HIST_COLS,
                      padding: '13px 22px',
                      alignItems: 'center',
                      borderBottom: i === messages.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                    }}
                  >
                    <span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, padding: '0 9px', borderRadius: 9999, background: 'var(--md-sys-color-secondary-container)', color: 'var(--md-sys-color-on-secondary-container)', font: '600 11px/1 var(--gs-font-kr)' }}>
                        <Sym name={cm.icon} size={12} fill={1} />
                        {cm.label}
                      </span>
                    </span>
                    <span style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{TARGET_LABELS[r.target]}</span>
                    <span style={{ font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>{r.content}</span>
                    <span className="gs-num" style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', textAlign: 'right' }}>{r.recipientCount.toLocaleString()}</span>
                    <span className="gs-num" style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{fmtAt(r.scheduledAt ?? r.createdAt)}</span>
                    <span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          height: 22,
                          padding: '0 9px',
                          borderRadius: 9999,
                          background: scheduled ? 'var(--gs-warning-container)' : 'var(--md-sys-color-primary-container)',
                          color: scheduled ? 'var(--gs-on-warning-container)' : 'var(--md-sys-color-on-primary-container)',
                          font: '600 11px/1 var(--gs-font-kr)',
                        }}
                      >
                        <Sym name={scheduled ? 'schedule' : 'check'} size={12} fill={1} />
                        {scheduled ? '예약' : '완료'}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      </ViewScroll>
    </>
  );
}
